import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getClientesEcommerce, getClientesLojaFisica, getClientesEcommerceOrders, getPedidoItens, getClientesLojaFisicaOrders, getClientesLojaFisicaPedidoItens, getCidadesDisponiveis } from '../../services/api';
import FiltrosClientes from '../../components/FiltrosClientes/FiltrosClientes';
import MapaDeCalor from '../../components/MapaDeCalor/MapaDeCalor';
import TopProdutosVistos from '../../components/TopProdutosVistos/TopProdutosVistos';
import WinBackExecutivo from '../../components/WinBackExecutivo/WinBackExecutivo';
import styles from './Clientes.module.css';

const Clientes = () => {
    const [activeTab, setActiveTab] = useState('ecommerce');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const limit = 50;

    // Novos estados de filtros (cidades agora é array)
    const [filtros, setFiltros] = useState({
        cidades: [],
        email: '',
        pedidosRange: '',
        dataInicio: '',
        dataFim: ''
    });

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: 'nome', direction: 'asc' });

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientOrders, setClientOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);

    // Items expansion state
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [ordersItems, setOrdersItems] = useState({});

    // Cidades disponíveis para o filtro
    const [cidadesDisponiveis, setCidadesDisponiveis] = useState([]);
    const [loadingItems, setLoadingItems] = useState({});

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setPage(1);
    }, [activeTab, debouncedSearch, filtros]);

    // Buscar cidades disponíveis quando a tab mudar
    useEffect(() => {
        const fetchCidades = async () => {
            if (activeTab === 'mapa') return;
            try {
                const fonte = activeTab === 'ecommerce' ? 'ecommerce' : 'loja_fisica';
                const loja = activeTab === 'ecommerce' ? null : activeTab;
                const response = await getCidadesDisponiveis(fonte, loja);
                if (response.success && response.data) {
                    setCidadesDisponiveis(response.data);
                }
            } catch (error) {
                console.error('Erro ao buscar cidades:', error);
            }
        };
        fetchCidades();
    }, [activeTab]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key) {
            direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
        } else {
            if (key === 'total_pedidos') direction = 'desc';
            else direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const fetchClientes = async () => {
        // Limitar cidades para evitar URL muito longa (erro 414)
        // Se mais de 50 cidades selecionadas, não envia filtro (equivale a não filtrar)
        const cidadesParaEnviar = Array.isArray(filtros.cidades) && filtros.cidades.length > 0 && filtros.cidades.length <= 50
            ? filtros.cidades.join(',')
            : '';

        const params = {
            page,
            limit,
            busca: debouncedSearch,
            sortBy: sortConfig.key,
            order: sortConfig.direction,
            cidades: cidadesParaEnviar,
            email: filtros.email,
            pedidosRange: filtros.pedidosRange,
            dataInicio: filtros.dataInicio,
            dataFim: filtros.dataFim
        };
        if (activeTab === 'ecommerce') {
            return await getClientesEcommerce(params);
        } else {
            return await getClientesLojaFisica({ ...params, loja: activeTab });
        }
    };

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['clientes', activeTab, page, debouncedSearch, sortConfig, filtros],
        queryFn: fetchClientes,
        keepPreviousData: true
    });

    const isCelular = (phone) => {
        if (!phone) return false;
        const cleanPhone = phone.replace(/\D/g, '');
        return cleanPhone.length === 11 && cleanPhone[2] === '9';
    };

    const handleOpenOrders = async (client) => {
        setSelectedClient(client);
        setIsModalOpen(true);
        setLoadingOrders(true);
        setOrdersItems({});
        setExpandedOrderId(null);

        try {
            let orders = [];
            if (activeTab === 'ecommerce') {
                orders = await getClientesEcommerceOrders(client.codigo_cliente || client.bling_id || client.id);
            } else {
                const clientId = client.codigo_cliente || client.id;
                orders = await getClientesLojaFisicaOrders(clientId, activeTab);
            }
            setClientOrders(orders);
        } catch (error) {
            console.error(error);
            setClientOrders([]);
        } finally {
            setLoadingOrders(false);
        }
    };

    const toggleOrderItems = async (orderId) => {
        if (expandedOrderId === orderId) {
            setExpandedOrderId(null);
            return;
        }

        setExpandedOrderId(orderId);

        if (!ordersItems[orderId] && !loadingItems[orderId]) {
            setLoadingItems(prev => ({ ...prev, [orderId]: true }));
            try {
                let items = [];
                if (activeTab === 'ecommerce') {
                    items = await getPedidoItens(orderId);
                } else {
                    items = await getClientesLojaFisicaPedidoItens(orderId);
                }
                setOrdersItems(prev => ({ ...prev, [orderId]: items }));
            } catch (error) {
                console.error(error);
            } finally {
                setLoadingItems(prev => ({ ...prev, [orderId]: false }));
            }
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedClient(null);
        setClientOrders([]);
        setExpandedOrderId(null);
    };

    const handleFilterChange = (newFiltros) => {
        setFiltros(prev => ({ ...prev, ...newFiltros }));
    };

    const handleSearch = (term) => {
        setSearch(term);
    };

    const [isExporting, setIsExporting] = useState(false);

    const handleExportCSV = async () => {
        setIsExporting(true);
        try {
            // Buscar TODOS os clientes (sem paginação) para exportação
            const apiParams = {
                page: 1,
                limit: 99999,
                sortBy: sortConfig.key,
                order: sortConfig.direction
            };

            // Aplicar filtros ativos
            if (debouncedSearch) apiParams.busca = debouncedSearch;
            if (filtros.cidades?.length > 0) apiParams.cidades = filtros.cidades.join(',');
            if (filtros.email) apiParams.email = filtros.email;
            if (filtros.pedidosRange) apiParams.pedidosRange = filtros.pedidosRange;
            if (filtros.dataInicio) apiParams.dataInicio = filtros.dataInicio;
            if (filtros.dataFim) apiParams.dataFim = filtros.dataFim;

            let allClientes;
            if (activeTab === 'ecommerce') {
                const result = await getClientesEcommerce(apiParams);
                allClientes = result?.data || [];
            } else {
                apiParams.loja = activeTab;
                const result = await getClientesLojaFisica(apiParams);
                allClientes = result?.data || [];
            }

            if (allClientes.length === 0) {
                alert('Nenhum cliente para exportar.');
                return;
            }

            const headers = ['NOME', 'CPF/CNPJ', 'TELEFONE', 'CIDADE', 'UF', 'EMAIL', 'PEDIDOS', 'DATA CADASTRO'];

            const csvRows = [
                headers.join(';'),
                ...allClientes.map(c => [
                    (c.nome || c.fantasia || '').replace(/;/g, ','),
                    c.cpf_cnpj || '',
                    c.telefone || c.celular || '',
                    c.cidade || '',
                    c.uf || '',
                    c.email || '',
                    c.total_pedidos || 0,
                    c.data_cadastro ? new Date(c.data_cadastro).toLocaleDateString() : ''
                ].join(';'))
            ];

            const csvContent = '\uFEFF' + csvRows.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `clientes_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Erro ao exportar clientes:', error);
            alert('Erro ao exportar. Tente novamente.');
        } finally {
            setIsExporting(false);
        }
    };

    const renderHeader = (label, sortKey) => (
        <th onClick={() => handleSort(sortKey)} className={styles.sortableHeader}>
            <div className={styles.headerContent}>
                {label}
                {sortConfig.key === sortKey && (
                    <span>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                )}
            </div>
        </th>
    );

    return (
        <div className={styles.container}>
            <div className={styles.pageHeader}>
                <h2>Gestão de Clientes</h2>
                <p className={styles.subtitle}>Gerencie os clientes das lojas</p>
            </div>

            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${styles.tabWinBack} ${activeTab === 'winback' ? styles.active : ''}`}
                    onClick={() => setActiveTab('winback')}
                >🔥 Win-Back</button>
                <button
                    className={`${styles.tab} ${activeTab === 'ecommerce' ? styles.active : ''}`}
                    onClick={() => setActiveTab('ecommerce')}
                >🛒 E-commerce</button>
                <button
                    className={`${styles.tab} ${activeTab === 'centro' ? styles.active : ''}`}
                    onClick={() => setActiveTab('centro')}
                >🏪 Loja Centro</button>
                <button
                    className={`${styles.tab} ${activeTab === 'caxias' ? styles.active : ''}`}
                    onClick={() => setActiveTab('caxias')}
                >🏬 Loja Caxias</button>
                <button
                    className={`${styles.tab} ${activeTab === 'mapa' ? styles.active : ''}`}
                    onClick={() => setActiveTab('mapa')}
                >🗺️ Mapa de Calor</button>
                <button
                    className={`${styles.tab} ${activeTab === 'produtos-vistos' ? styles.active : ''}`}
                    onClick={() => setActiveTab('produtos-vistos')}
                >📊 Produtos Vistos</button>
            </div>

            {activeTab === 'winback' ? (
                <WinBackExecutivo />
            ) : activeTab === 'mapa' ? (
                <MapaDeCalor />
            ) : activeTab === 'produtos-vistos' ? (
                <TopProdutosVistos />
            ) : (
                <>

                    <FiltrosClientes
                        onFilterChange={handleFilterChange}
                        onSearch={handleSearch}
                        onExportCSV={handleExportCSV}
                        isExporting={isExporting}
                        activeTab={activeTab}
                        initialSearch={search}
                        initialCidades={filtros.cidades}
                        initialEmail={filtros.email}
                        initialPedidosRange={filtros.pedidosRange}
                        initialDataInicio={filtros.dataInicio}
                        initialDataFim={filtros.dataFim}
                        cidadesDisponiveis={cidadesDisponiveis}
                    />

                    {/* Stats Cards */}
                    {data?.pagination && (
                        <div className={styles.statsRow}>
                            <div className={styles.statCard}>
                                <div className={styles.statValue}>{data.pagination.total?.toLocaleString('pt-BR') || 0}</div>
                                <div className={styles.statLabel}>Total de Clientes</div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statValue}>{data.data?.length || 0}</div>
                                <div className={styles.statLabel}>Exibindo</div>
                            </div>
                        </div>
                    )}

                    {isLoading ? <div className={styles.loading}>Carregando...</div> : isError ? <div className={styles.error}>Erro ao carregar clientes</div> : (
                        <>
                            <div className={styles.tableWrapper}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            {renderHeader('Nome / Fantasia', 'nome')}
                                            {renderHeader('CPF / CNPJ', 'cpf_cnpj')}
                                            {renderHeader('Telefone', 'telefone')}
                                            <th>Cidade / UF</th>
                                            {renderHeader('Email', 'email')}
                                            {renderHeader('Pedidos', 'total_pedidos')}
                                            {renderHeader('Data Cadastro', 'data_cadastro')}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.data?.length > 0 ? data.data.map(c => (
                                            <tr key={c.id || c.codigo_cliente}>
                                                <td className={styles.clientName}>{c.nome || c.fantasia}</td>
                                                <td>{c.cpf_cnpj || '-'}</td>
                                                <td
                                                    onClick={() => {
                                                        const phone = c.telefone || c.celular;
                                                        if (!phone) return;
                                                        const cleanPhone = phone.replace(/\D/g, '');
                                                        if (cleanPhone.length === 11 && cleanPhone[2] === '9') {
                                                            window.open(`https://wa.me/55${cleanPhone}`, '_blank');
                                                        }
                                                    }}
                                                    className={isCelular(c.telefone || c.celular) ? styles.whatsappLink : ''}
                                                    title={isCelular(c.telefone || c.celular) ? "Abrir WhatsApp" : ""}
                                                >
                                                    {c.telefone || c.celular || '-'}
                                                </td>
                                                <td>{c.cidade ? `${c.cidade}/${c.uf}` : '-'}</td>
                                                <td className={styles.emailCell}>{c.email || '-'}</td>
                                                <td
                                                    className={styles.pedidosCell}
                                                    onClick={() => (c.total_pedidos > 0 || activeTab !== 'ecommerce') && handleOpenOrders(c)}
                                                    title={c.total_pedidos > 0 ? `Ver ${c.total_pedidos} pedido(s)` : (activeTab !== 'ecommerce' ? 'Buscar pedidos' : 'Sem pedidos')}
                                                >
                                                    <span className={`${styles.pedidosBadge} ${c.total_pedidos > 0 ? styles.hasPedidos : ''}`}>
                                                        {c.total_pedidos !== undefined ? c.total_pedidos : (activeTab !== 'ecommerce' ? '...' : 0)}
                                                    </span>
                                                </td>
                                                <td>{c.data_cadastro ? new Date(c.data_cadastro).toLocaleDateString() : '-'}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="7" className={styles.emptyState}>
                                                    Nenhum cliente encontrado.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {data?.pagination && (
                                <div className={styles.pagination}>
                                    <button disabled={page === 1} onClick={() => setPage(page - 1)}>Anterior</button>
                                    <span>Página {page} de {data.pagination.totalPages || 1} (Total: {data.pagination.total})</span>
                                    <button disabled={page >= (data.pagination.totalPages || 1)} onClick={() => setPage(page + 1)}>Próximo</button>
                                </div>
                            )}
                        </>
                    )}

                    {isModalOpen && (
                        <div className={styles.modalOverlay} onClick={closeModal}>
                            <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                                <button className={styles.closeButton} onClick={closeModal}>×</button>
                                <div className={styles.orderHeader}>
                                    <h3>Pedidos de {selectedClient?.nome}</h3>
                                    <span style={{ fontSize: '14px', color: '#666' }}>
                                        {activeTab === 'ecommerce' ? 'E-commerce' : activeTab === 'centro' ? 'Loja Centro' : 'Loja Caxias'}
                                    </span>
                                </div>

                                {loadingOrders ? (
                                    <div style={{ textAlign: 'center' }}>Carregando pedidos...</div>
                                ) : clientOrders.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: '#666' }}>
                                        Nenhum pedido encontrado para este cliente.
                                    </div>
                                ) : (
                                    <table className={styles.table} style={{ fontSize: '14px' }}>
                                        <thead>
                                            <tr>
                                                <th>{activeTab === 'ecommerce' ? 'ID Pedido' : 'Cupom/Boleta'}</th>
                                                <th>Data</th>
                                                <th>Itens</th>
                                                <th>Valor Total</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {clientOrders.map((p, idx) => (
                                                <React.Fragment key={idx}>
                                                    <tr
                                                        onClick={() => toggleOrderItems(p.pedido_id)}
                                                        style={{ cursor: 'pointer', backgroundColor: expandedOrderId === p.pedido_id ? '#f0f8ff' : 'transparent' }}
                                                        className={styles.pRow}
                                                    >
                                                        <td style={{ fontWeight: expandedOrderId === p.pedido_id ? 'bold' : 'normal' }}>
                                                            <span style={{ display: 'inline-block', width: '20px', color: '#666' }}>
                                                                {expandedOrderId === p.pedido_id ? '▼' : '▶'}
                                                            </span>
                                                            {p.pedido_id}
                                                        </td>
                                                        <td>{p.data_venda ? new Date(p.data_venda).toLocaleDateString() : '-'}</td>
                                                        <td style={{ color: '#666', fontSize: '13px' }}>
                                                            {p.itens_count > 0 ? `${p.itens_count} itens` : 'Ver Itens'}
                                                        </td>
                                                        <td>R$ {parseFloat(p.valor_total || 0).toFixed(2).replace('.', ',')}</td>
                                                        <td>{p.status || '-'}</td>
                                                    </tr>
                                                    {expandedOrderId === p.pedido_id && (
                                                        <tr>
                                                            <td colSpan="5" style={{ padding: '10px 20px', backgroundColor: '#fafafa', borderBottom: '2px solid #eee' }}>
                                                                {loadingItems[p.pedido_id] ? (
                                                                    <div style={{ padding: '15px', textAlign: 'center', color: '#666' }}>Carregando itens...</div>
                                                                ) : ordersItems[p.pedido_id]?.length > 0 ? (
                                                                    <div style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)', borderRadius: '4px', overflow: 'hidden' }}>
                                                                        <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse', background: 'white' }}>
                                                                            <thead>
                                                                                <tr style={{ background: '#f5f5f5', color: '#555' }}>
                                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600' }}>Produto</th>
                                                                                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '600', width: '80px' }}>Qtd</th>
                                                                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600', width: '100px' }}>Unit.</th>
                                                                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '600', width: '100px' }}>Total</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {ordersItems[p.pedido_id].map((item, i) => (
                                                                                    <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                                                                                        <td style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                                            {item.imagem_url ? (
                                                                                                <img
                                                                                                    src={item.imagem_url}
                                                                                                    alt={item.descricao}
                                                                                                    title={item.descricao}
                                                                                                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #eee' }}
                                                                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                                                                />
                                                                                            ) : null}
                                                                                            <div style={{ width: '50px', height: '50px', background: '#f5f5f5', borderRadius: '4px', display: item.imagem_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: '10px' }}>
                                                                                                Sem Foto
                                                                                            </div>
                                                                                            <span style={{ lineHeight: '1.3' }}>{item.descricao}</span>
                                                                                        </td>
                                                                                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>{item.quantidade}</td>
                                                                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>R$ {parseFloat(item.valor_unitario).toFixed(2).replace('.', ',')}</td>
                                                                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>R$ {parseFloat(item.valor_total).toFixed(2).replace('.', ',')}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ padding: '15px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                                                                        Nenhum item encontrado para este pedido.
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Clientes;
